// useLoginModalState.js

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useLoginModalState
 *
 * Manages local wizard navigation, error state, and signup-email focus behavior for Login.
 *
 * @param {object} params - Hook parameters.
 * @param {number} params.initialStep - Initial wizard step when the modal opens.
 *
 * @returns {object} Grouped local UI state and handlers for Login.
 */
export default function useLoginModalState({ initialStep }) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [focusSignupEmailOnStepEnter, setFocusSignupEmailOnStepEnter] = useState(false);

  const signupEmailInputRef = useRef(null);

  /**
   * nextStep
   *
   * Advances the login wizard forward one step and clears the current error.
   */
  const nextStep = useCallback(() => {
    setDirection(1);
    setStep((prev) => prev + 1);
    setErrorMessage(null);
  }, []);

  /**
   * prevStep
   *
   * Moves the login wizard back one step and clears the current error.
   */
  const prevStep = useCallback(() => {
    setDirection(-1);
    setStep((prev) => prev - 1);
    setErrorMessage(null);
  }, []);

  /**
   * handleSignupEmailStepClick
   *
   * Advances to the signup step and requests focus for the signup email field.
   */
  const handleSignupEmailStepClick = useCallback(() => {
    setFocusSignupEmailOnStepEnter(true);
    nextStep();
  }, [nextStep]);

  /**
   * handleForgotPasswordStepClick
   *
   * Advances to the forgot-password step.
   */
  const handleForgotPasswordStepClick = useCallback(() => {
    setStep(3);
    setDirection(1);
    setErrorMessage(null);
  }, []);

  /**
   * handleForgotPasswordBack
   *
   * Returns from forgot-password to the sign-in step.
   */
  const handleForgotPasswordBack = useCallback(() => {
    setStep(1);
    setDirection(-1);
    setErrorMessage(null);
  }, []);

  useEffect(() => {
    if (step !== 2 || !focusSignupEmailOnStepEnter) return;

    requestAnimationFrame(() => {
      signupEmailInputRef.current?.focus();
    });

    setFocusSignupEmailOnStepEnter(false);
  }, [focusSignupEmailOnStepEnter, step]);

  /**
   * resetForOpen
   *
   * Resets the local modal UI state when the login modal opens.
   */
  const resetForOpen = useCallback(() => {
    setStep(initialStep);
    setDirection(0);
    setFocusSignupEmailOnStepEnter(false);
    setErrorMessage(null);
  }, [initialStep]);

  return {
    modalUiState: {
      errorMessage,
      setErrorMessage,
      step,
      direction,
      isSignupStep: step === 2,
      isForgotStep: step === 3,
      signupEmailInputRef,
    },
    modalUiHandlers: {
      prevStep,
      handleSignupEmailStepClick,
      handleForgotPasswordStepClick,
      handleForgotPasswordBack,
      resetForOpen,
    },
  };
}